import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { contributorColumns } from '../../GscTools/columns';
import { formatMetric } from '../../GscTools/format';
import { reportsPosition } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { VIEW } from '../scope';

/**
 * Block 03 — the rows that contribute to the selected one: the queries behind a page, or the
 * pages behind a query.
 *
 * Nothing is requested until a row is selected, and what was requested for a previous selection
 * is discarded rather than left on screen under a new one. The block compares its own total
 * against the selected row's figure instead of hiding the difference: Search Console withholds
 * query rows below its privacy thresholds, so these contributors are not expected to add up, and
 * the shortfall is information that was withheld rather than traffic that is missing.
 */
class Contributors extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
    }

    searchFunction(data, search) {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter((item) => item.key?.toLowerCase().includes(term));
    }

    render() {
        const { t, scope, status, error, rows, totals, selectedKey, selectedRow, period } =
            this.props;

        const isPages = scope.view === VIEW.PAGES;
        const columns = contributorColumns(t, {
            hasPosition: reportsPosition(scope.searchType),
            keyLabel: isPages ? t('tools.gsc.columns.query') : t('tools.gsc.columns.page'),
            isUrl: !isPages,
        });

        const rowClicks = selectedRow?.current?.clicks ?? null;
        const covered =
            totals && rowClicks !== null && rowClicks > 0
                ? Math.min(100, (100 * totals.clicks) / rowClicks)
                : null;

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">
                            {isPages
                                ? t('tools.pages_queries_changes.contributors.title_queries')
                                : t('tools.pages_queries_changes.contributors.title_pages')}
                        </p>
                        <p className="gsc-tool-sub">
                            {selectedKey
                                ? `${selectedKey} · ${formatPeriod(period)}`
                                : t('tools.pages_queries_changes.contributors.description')}
                        </p>
                    </div>
                </div>

                <div className="card-body">
                    {!selectedKey && (
                        <div className="gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-fi_search" />
                            </span>
                            <h4>{t('tools.pages_queries_changes.contributors.idle_title')}</h4>
                            <p>{t('tools.pages_queries_changes.contributors.idle')}</p>
                        </div>
                    )}

                    {!!selectedKey && status === 'loading' && <Loading padding={20} />}

                    {!!selectedKey && status === 'error' && (
                        <div className="gsc-tool-banner error">
                            {error ?? t('global.something_went_wrong')}
                        </div>
                    )}

                    {!!selectedKey && status === 'done' && (
                        <>
                            <DataTable
                                nodes={rows}
                                COLUMNS={columns}
                                sortFns={getSortKeys(columns)}
                                additionalStyles={setAdditionalStyles(columns)}
                                searchFunction={this.searchFunction}
                                defaultSortKey="clicks"
                                fileName={t('tools.pages_queries_changes.contributors.file')}
                                customEmptyMessage={t(
                                    'tools.pages_queries_changes.contributors.empty',
                                )}
                                customContainerStyles="gsc-tool-key-table"
                            />

                            <p className="gsc-tool-table-note">
                                {covered === null
                                    ? t('tools.pages_queries_changes.contributors.privacy')
                                    : t('tools.pages_queries_changes.contributors.covered', {
                                          rows: rows.length,
                                          clicks: formatMetric('clicks', totals.clicks),
                                          total: formatMetric('clicks', rowClicks),
                                          percent: covered.toFixed(0),
                                      })}{' '}
                                {t('tools.pages_queries_changes.contributors.privacy_short')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(Contributors);
